import React, {
  createContext,
  useState,
  useMemo,
  memo,
  useCallback,
  useEffect
} from "react";
import { IFileData, HGenerateFileData } from "./helpers/create-file-data";
import { MxFileDraggableConfig, MxFileDropzoneProps } from "./model/types.model";
import { getValidationErrorFileDataEnhanced } from "./helpers/getValidationError";
import toast from "react-hot-toast";
import useOptimiceImageFile from "@rf-hooks/useOptimiseImageFile";

type omit = "children" | "values" | "onChange" | "onBlur" | "inputRef";

export type MxFileDropzoneContextReturn = {
  files: IFileData[];
  addFile: (file: File, config: MxFileDraggableConfig) => void;
  removeFile: (fileIndex: number) => IFileData /**Remove file from the list*/;
  isDragging: boolean;
  setFiles: React.Dispatch<React.SetStateAction<IFileData[]>>;
  openFileExplore: (e: any) => void;
  uniqueId: string;
  isFileExplorerOpen: boolean;
  ctx: MxFileDropzoneProps;
} & Omit<MxFileDropzoneProps, omit>;

export const MxFileDraggableContext =
  createContext<MxFileDropzoneContextReturn | null>(null);

const MxFileDropzone: React.FC<MxFileDropzoneProps> = props => {
  const { optimiceImageFile } = useOptimiceImageFile();
  const fileInputIdSelector = useMemo(() => window.crypto.randomUUID(), []);
  const [files, setFiles] = useState<IFileData[]>(props.values || []);
  const [isDragging, setIsDragging] = useState(false);
  const [isFileExplorerOpen, setIsFileExplorerOpen] = useState(false);

  const processFile = async (file: File): Promise<IFileData | null> => {
    try {
      const error = await getValidationErrorFileDataEnhanced(
        file,
        props.maxSize,
        props.minSize,
        props.allowedTypes
      );

      if (error) {
        toast.error(error, {
          duration: 5000,
          position: "top-right"
        });
        return null;
      }

      let processedFile = file;
      if (file.type.includes("image")) {
        processedFile = await optimiceImageFile(file);
      }

      const newFile = HGenerateFileData({
        file: processedFile
      });

      if (!newFile) {
        throw new Error("Error al generar el archivo");
      }

      return newFile;
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error(`Error al procesar el archivo: ${file.name}`);
      return null;
    }
  };

  const addFile = async (file: File) => {
    try {
      props.onLoadFileStart && props.onLoadFileStart(file);
      const processedFile = await processFile(file);

      if (!processedFile) {
        return;
      }

      setFiles(prevFiles => {
        const newState = !props.multiple
          ? [processedFile]
          : [...prevFiles, processedFile];
        const newValue = {
          target: {
            name: props.name,
            value: newState
          }
        };

        props.onChange && props.onChange(newValue);
        props.onBlur && props.onBlur(newValue);
        return newState;
      });
    } finally {
      props.onLoadFileEnd && props.onLoadFileEnd(file);
    }
  };

  const processMultipleFiles = async (files: File[]) => {
    const results = await Promise.allSettled(files.map(file => processFile(file)));

    const successfulFiles = results
      .filter(
        (result): result is PromiseFulfilledResult<IFileData> =>
          result.status === "fulfilled" && result.value !== null
      )
      .map(result => result.value);

    if (successfulFiles.length > 0) {
      setFiles(prevFiles => {
        const newState = !props.multiple
          ? [successfulFiles[0]]
          : [...prevFiles, ...successfulFiles];

        const newValue = {
          target: {
            name: props.name,
            value: newState
          }
        };

        props.onChange && props.onChange(newValue);
        props.onBlur && props.onBlur(newValue);
        return newState;
      });
    }

    return successfulFiles.length;
  };

  const removeFile = useCallback(
    (fileIndex: number): IFileData => {
      const itemToDelete = files[fileIndex];
      if (!itemToDelete) {
        throw new Error(`No se encontró el archivo en el índice ${fileIndex}`);
      }

      setFiles(prevFiles => {
        const newState = prevFiles.filter((_, index) => index !== fileIndex);
        const newValue = {
          target: {
            name: props.name,
            value: newState
          }
        };

        props.onChange && props.onChange(newValue);
        props.onBlur && props.onBlur(newValue);
        return newState;
      });

      return itemToDelete;
    },
    [files, props.name, props.onChange, props.onBlur]
  );

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (files.length === 0 || e.dataTransfer.items[0].kind === "file") {
      const droppedFiles = Array.from(e.dataTransfer.files);
      await processMultipleFiles(droppedFiles);
    }
  }, []);

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      await processMultipleFiles(selectedFiles);
      setIsFileExplorerOpen(false);
      e.target.value = "";
    },
    []
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (files.length === 0 || e.dataTransfer.items[0].kind === "file") {
        setIsDragging(true);
      }
    },
    [files]
  );

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (files.length === 0 || e.target === e.currentTarget) {
        setIsFileExplorerOpen(true);
        document.getElementById(fileInputIdSelector)?.click();
      }
    },
    [files]
  );

  const openFileExplore = useCallback(
    (e?: React.MouseEvent<HTMLDivElement>) => {
      if (files.length > 0 || e?.target === e?.currentTarget) {
        setIsFileExplorerOpen(true);
        document.getElementById(fileInputIdSelector)?.click();
      }
    },
    [files]
  );

  const contextValue = useMemo(
    () => ({
      files,
      addFile,
      removeFile,
      isDragging,
      maxSize: props.maxSize,
      minSize: props.minSize,
      allowedTypes: props.allowedTypes,
      label: props.label,
      disabled: props.disabled,
      invalid: props.invalid,
      name: props.name,
      setFiles,
      openFileExplore,
      uniqueId: fileInputIdSelector,
      isFileExplorerOpen,
      ctx: props
    }),
    [
      JSON.stringify(files),
      isDragging,
      props.disabled,
      props.invalid,
      fileInputIdSelector,
      isFileExplorerOpen
    ]
  );

  useEffect(() => {
    if (JSON.stringify(files) != JSON.stringify(props.values)) {
      setFiles(props.values || []);
    }
  }, [JSON.stringify(props.values)]);
  return (
    <MxFileDraggableContext.Provider value={contextValue}>
      <div
        className={`drop-zone ${isDragging ? "drag-over" : ""}`}
        onDragOver={props.disabled ? undefined : handleDragOver}
        onDragLeave={props.disabled ? undefined : handleDragLeave}
        onDrop={props.disabled ? undefined : handleDrop}
        onClick={props.disabled ? undefined : handleClick}
        style={{ height: "auto", position: "relative", zIndex: 1 }}
        data-testid={`${props.name}-file-draggable`}
      >
        {props.children}
      </div>
      <input
        type="file"
        id={fileInputIdSelector}
        style={{ display: "none" }}
        name={props.name}
        multiple={props.multiple}
        onBlur={props.onBlur}
        onChange={handleFileInputChange}
        ref={props.inputRef}
        accept={props.allowedTypes.join(",")}
        disabled={props.disabled}
        data-testid={`${props.name}-file-draggable-input`}
      />
    </MxFileDraggableContext.Provider>
  );
};

export default memo(MxFileDropzone);
